import { useState, useCallback, useEffect } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { useTheme } from "./ThemeProvider";

export function SpeakButton({ text, label, className = "" }) {
  const [speaking, setSpeaking] = useState(false);
  const { theme } = useTheme();
  const isCalm = theme === "calm";

  useEffect(() => {
    return () => { window.speechSynthesis?.cancel(); };
  }, []);

  const toggle = useCallback(() => {
    const synth = window.speechSynthesis;
    if (!synth) return;

    if (speaking) {
      synth.cancel();
      setSpeaking(false);
      return;
    }

    synth.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "es-ES";
    utterance.rate = isCalm ? 0.85 : 0.95;
    utterance.pitch = 1;

    // Try to find a Spanish voice
    const voices = synth.getVoices();
    const esVoice = voices.find(v => v.lang.startsWith("es"));
    if (esVoice) utterance.voice = esVoice;

    utterance.onend = () => setSpeaking(false);
    utterance.onerror = () => setSpeaking(false);

    setSpeaking(true);
    synth.speak(utterance);
  }, [text, speaking, isCalm]);

  if (!window.speechSynthesis) return null;

  return (
    <button
      onClick={(e) => { e.stopPropagation(); toggle(); }}
      className={`inline-flex items-center gap-1.5 rounded-full transition-all ${
        speaking
          ? "bg-[var(--blue-vibrant)] text-white px-3 py-1.5"
          : isCalm
            ? "bg-[var(--blue-vibrant)]/10 text-[var(--blue-vibrant)] hover:bg-[var(--blue-vibrant)]/20 px-3 py-1.5"
            : "text-[var(--text-muted)] hover:text-[var(--blue-vibrant)] hover:bg-[var(--blue-vibrant)]/10 p-1.5"
      } ${className}`}
      title={speaking ? "Detener" : "Escuchar"}
      data-testid="speak-button"
    >
      {speaking ? (
        <VolumeX className={isCalm ? "w-4 h-4" : "w-3.5 h-3.5"} />
      ) : (
        <Volume2 className={isCalm ? "w-4 h-4" : "w-3.5 h-3.5"} />
      )}
      {(isCalm || speaking) && (
        <span className="text-xs font-medium">
          {speaking ? "Detener" : label || "Escuchar"}
        </span>
      )}
    </button>
  );
}
